import {useCallback, useMemo, useSyncExternalStore} from 'react'

import {useBoardDataContext} from '../provider/contexts'
import {useBoardFocusGrid, useBoardFocusStore} from './contexts'

/** Where a window has to move to before the element focus is owed to can exist. */
export type BoardFocusWindowTarget = {
  /** The index of the column the position names. */
  columnIndex: number
  /** The index of the row it names. */
  rowIndex: number
  /**
   * Focus has been asked for and nothing has taken it yet.
   *
   * A window moves for this and never for the position alone. The position also moves
   * when a reader clicks a card, and when new data shifts what it was pointing at, and
   * dragging the board to either of those would take a reader somewhere they did not ask
   * to go.
   */
  isPending: boolean
}

/**
 * The board's focus position as a window has to read it: two indexes, and whether an
 * element still owes it focus.
 *
 * **Call this from a component that renders nothing.** Unlike the rest of the focus
 * subscriptions, this one answers with a new value on every move, so a component that
 * draws anything would redraw all of it every time a reader pressed an arrow key -- the
 * exact cost the focus store exists to avoid. What it is for is a null-rendering sibling
 * that moves a scroll container and returns.
 */
export function useBoardFocusWindowTarget(): BoardFocusWindowTarget {
  const {getState, subscribe} = useBoardFocusStore()
  const {rowKeys} = useBoardFocusGrid()
  const {data} = useBoardDataContext()
  const rowIndexesByKey = useMemo(() => new Map(data.rows.map((row, index) => [row.key, index])), [data.rows])

  // The state object itself, which the reducer returns untouched whenever a move settled
  // where it started, so this is stable between real changes.
  const getSnapshot = useCallback(() => getState(), [getState])
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  return useMemo(() => {
    // Focus navigation indexes only expanded rows, while the virtualizer indexes every
    // row because collapsed headers remain rendered. Translate through the row key so a
    // pending focus request scrolls to the same row in both models.
    const rowKey = rowKeys[state.anchor.rowIndex]

    return {
      columnIndex: state.anchor.columnIndex,
      rowIndex: rowKey === undefined ? state.anchor.rowIndex : (rowIndexesByKey.get(rowKey) ?? state.anchor.rowIndex),
      isPending: state.pendingFocus,
    }
  }, [rowIndexesByKey, rowKeys, state])
}
